import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MatrixRain from "./MatrixRain";

/**
 * Epic Sofia wake-up sequence - showcases her intelligence
 * Matrix-style with dramatic reveals
 */
export default function SofiaWakeUp({ onComplete }) {
  const [stage, setStage] = useState(0);

  const stages = [
    {
      title: "INITIALIZING...",
      subtitle: "Neural Network Activation",
      stats: ["1,247,893 property records loaded", "31,763 broker connections active", "Real-time market data synced"],
    },
    {
      title: "SOFIA ONLINE",
      subtitle: "AI Concierge Ready",
      stats: [
        "GPT-4 powered conversation engine",
        "Voice recognition: Active",
        "Knowledge base: Dubai & RAK real estate",
        "Language support: EN, AR, RU",
      ],
    },
    {
      title: "READY TO ASSIST",
      subtitle: "Your Personal Real Estate Intelligence",
      stats: [
        "Bay Views allocation: 12 units",
        "Market insights: Updated 2026-04-29",
        "Market data: Live",
        "RERA 41793 verified",
      ],
    },
  ];

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 2000),
      setTimeout(() => setStage(2), 4000),
      setTimeout(() => onComplete(), 6000),
    ];

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[10001] bg-ink overflow-hidden flex items-center justify-center">
      {/* Matrix Rain Background */}
      <MatrixRain />

      {/* Content */}
      <div className="relative z-10 text-center max-w-4xl px-8">
        <AnimatePresence mode="wait">
          {stage < stages.length && (
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Main Title */}
              <motion.h1
                className="font-display text-6xl sm:text-8xl text-gold tracking-wider"
                animate={{
                  textShadow: [
                    "0 0 20px rgba(201, 168, 106, 0.5)",
                    "0 0 40px rgba(201, 168, 106, 0.8)",
                    "0 0 20px rgba(201, 168, 106, 0.5)",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {stages[stage].title}
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                className="text-bone/80 text-2xl tracking-widest uppercase"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {stages[stage].subtitle}
              </motion.p>

              {/* Stats Grid */}
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                {stages[stage].stats.map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + i * 0.15 }}
                    className="bg-ink-light/50 backdrop-blur-sm border border-gold/20 p-4 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-gold rounded-full animate-pulse" />
                      <span className="text-bone/90 text-sm font-mono">{stat}</span>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Sofia Logo - Appears in final stage */}
              {stage === 2 && (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.8, ease: "backOut" }}
                  className="mt-12"
                >
                  <img
                    src="/sofia-logo.webp"
                    alt="Sofia"
                    className="w-32 h-32 mx-auto filter drop-shadow-glow-gold"
                  />
                </motion.div>
              )}

              {/* Progress Bar */}
              <motion.div className="w-full h-1 bg-ink-light/30 overflow-hidden mt-8">
                <motion.div
                  className="h-full bg-gradient-to-r from-gold/50 via-gold to-gold/50"
                  animate={{
                    x: ["-100%", "100%"],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
